/**
 * InvestmentItemsListPage - Main list view for investment items
 *
 * Features:
 * - UniversalTable with lazy loading
 * - FilterBar with smart filtering
 * - Multi-select rows for bulk operations
 * - Statistics showing total cost and counts by status
 * - Convert selected approved items to renovation project
 * - Create/Edit/Delete individual items
 */

import { memo, useState, useCallback, useMemo, useEffect } from "react";
import { Wallet, Plus, Rocket, Trash, Warning, SquaresFour } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { FilterBar } from "shared/components/filterBar";
import {
  UniversalTable,
  LazyLoadTrigger,
} from "shared/components/UniversalTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { useInvestmentItems } from "./hooks/useInvestmentItems";
import { createInvestmentItemCells } from "./shared/investmentCellFactory";
import { buildInvestmentFooterCells } from "./shared/investmentFooterCells";
import {
  useBudgetBuckets,
  dispatchBudgetBucketsChanged,
} from "./hooks/useBudgetBuckets";
import { INVESTMENT_TABLE_CONFIG } from "./configs/investmentTableConfig";
import { useUsers } from "shared/contexts/users.context";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  InvestmentItem,
  InvestmentItemStatus,
  RenovationLabel,
  INVESTMENT_STATUS_LABELS,
} from "./types/investment";
import { InvestmentItemModal } from "./components/InvestmentItemModal";
import { ReleaseInvestmentItemDialog } from "./components/ReleaseInvestmentItemDialog";
import { BucketAllocationDialog } from "./components/BucketAllocationDialog";
import { BudgetSplitDesigner } from "./components/BudgetSplitDesigner";
import { ConvertToProjectModal } from "./components/ConvertToProjectModal";
import { ChangeWithReasonModal } from "./components/ChangeWithReasonModal";
import { BudgetTransferModal } from "./components/BudgetTransferModal";
import { useBuildingsAndUnits } from "hooks/useBuildingsAndUnits";

interface InvestmentItemsListPageProps {
  /** Initial item ID to open in detail panel (for deep linking) */
  initialOpenItemId?: number;
}

const InvestmentItemsListPageComponent = ({ initialOpenItemId }: InvestmentItemsListPageProps) => {
  const { users } = useUsers();
  const { session } = useSession();
  const { buildings, units } = useBuildingsAndUnits();

  // Renovation labels state (for label picker)
  const [renovationLabels, setRenovationLabels] = useState<RenovationLabel[]>([]);

  // Local items state for optimistic updates
  const [localItems, setLocalItems] = useState<InvestmentItem[]>([]);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [buildingFilter, setBuildingFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [hideConverted, setHideConverted] = useState(true); // Hide converted items by default
  const [labelFilter, setLabelFilter] = useState<number | undefined>();
  const [labelTypeFilter, setLabelTypeFilter] = useState<string | undefined>();
  const [yearFilter, setYearFilter] = useState<number | undefined>();
  const [unitFilter, setUnitFilter] = useState<string | undefined>();

  // Fetch data with filters
  const {
    items: fetchedItems,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    fetchItems,
    observerTarget,
    hasActiveFilters,
  } = useInvestmentItems({
    searchText,
    buildingId: buildingFilter,
    status: statusFilter,
    excludeConverted: hideConverted,
    labelId: labelFilter,
    labelType: labelTypeFilter,
    createdYear: yearFilter,
    unitId: unitFilter,
  });

  // Sync local items with fetched items
  useEffect(() => {
    setLocalItems(fetchedItems);
  }, [fetchedItems]);

  // Fetch renovation labels for the label picker
  useEffect(() => {
    const fetchLabels = async () => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/labels`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setRenovationLabels(data || []);
        }
      } catch (error) {
        console.error("Failed to fetch renovation labels:", error);
      }
    };
    fetchLabels();
  }, [session]);

  // Listen for budget change events from the table cell
  useEffect(() => {
    const handleBudgetChangeEvent = (event: CustomEvent) => {
      const { item } = event.detail;
      if (item) {
        setBudgetChangeItem(item);
        setBudgetChangeModalOpen(true);
      }
    };

    window.addEventListener(
      "investment-budget-change",
      handleBudgetChangeEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "investment-budget-change",
        handleBudgetChangeEvent as EventListener
      );
    };
  }, []);

  // Listen for status change events from the table cell
  useEffect(() => {
    const handleStatusChangeEvent = (event: CustomEvent) => {
      const { item } = event.detail;
      if (item) {
        setStatusChangeItem(item);
        setStatusChangeModalOpen(true);
      }
    };

    window.addEventListener(
      "investment-status-change",
      handleStatusChangeEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "investment-status-change",
        handleStatusChangeEvent as EventListener
      );
    };
  }, []);

  // Listen for bucket-allocation events fired by the bucket cell.
  const [bucketAllocItem, setBucketAllocItem] = useState<InvestmentItem | null>(
    null,
  );
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const { item } = event.detail || {};
      if (item) setBucketAllocItem(item);
    };
    window.addEventListener(
      "investment-bucket-allocation",
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        "investment-bucket-allocation",
        handler as EventListener,
      );
    };
  }, []);

  // Use local items for display (allows optimistic updates)
  const items = localItems;

  // Selection state
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InvestmentItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<InvestmentItem | null>(null);
  const [releasingItem, setReleasingItem] = useState<InvestmentItem | null>(null);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [budgetChangeModalOpen, setBudgetChangeModalOpen] = useState(false);
  const [budgetChangeItem, setBudgetChangeItem] = useState<InvestmentItem | null>(null);
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [statusChangeItem, setStatusChangeItem] = useState<InvestmentItem | null>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSourceItem, setTransferSourceItem] = useState<InvestmentItem | null>(null);
  const [transferFreedAmount, setTransferFreedAmount] = useState(0);
  const [splitDesignerBucketId, setSplitDesignerBucketId] = useState<number | null>(null);

  // Group by state
  const [groupBy] = useState<string>("none");

  // Deep-link: when ?item=<id> is set, open the edit modal once the item
  // shows up in the loaded list. The list fetch is the first thing the page
  // does, so this resolves on the same render cycle in the typical flow.
  useEffect(() => {
    if (!initialOpenItemId) return;
    const itemsArray = Array.isArray(items) ? items : [];
    const found = itemsArray.find((i: any) => i.id === initialOpenItemId);
    if (found) {
      setEditingItem(found as InvestmentItem);
      setEditModalOpen(true);
    }
  }, [initialOpenItemId, items]);

  // Handle modal success - refresh the list
  const handleModalSuccess = useCallback(() => {
    fetchItems(false); // Refresh the list
  }, [fetchItems]);

  // Handle budget transfer request (from ChangeWithReasonModal after budget decrease)
  const handleTransferRequest = useCallback((item: InvestmentItem, freedAmount: number) => {
    setTransferSourceItem(item);
    setTransferFreedAmount(freedAmount);
    setTransferModalOpen(true);
  }, []);

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats = {
      totalCost: 0,
      byStatus: {} as Record<InvestmentItemStatus, { count: number; cost: number }>,
    };

    // Initialize status counts
    Object.values(InvestmentItemStatus).forEach((status) => {
      stats.byStatus[status] = { count: 0, cost: 0 };
    });

    // Guard against items not being an array (during initial load)
    const itemsArray = Array.isArray(items) ? items : [];

    itemsArray.forEach((item) => {
      const cost = Number(item.estimatedCostExclVat) || 0;
      stats.totalCost += cost;
      if (stats.byStatus[item.status]) {
        stats.byStatus[item.status].count++;
        stats.byStatus[item.status].cost += cost;
      }
    });

    // Ensure totalCost is a valid number
    if (isNaN(stats.totalCost)) {
      stats.totalCost = 0;
    }

    return stats;
  }, [items]);

  // Calculate selected items statistics
  const selectedItemsStats = useMemo(() => {
    // Guard against items not being an array
    const itemsArray = Array.isArray(items) ? items : [];
    const selectedItems = itemsArray.filter((item) => selectedRows.has(item.id));
    const totalCost = selectedItems.reduce(
      (sum, item) => sum + (Number(item.estimatedCostExclVat) || 0),
      0
    );

    // Get building ID from item (check both buildingId and building.id)
    const getBuildingId = (item: InvestmentItem) =>
      item.buildingId || item.building?.id || "";

    // Check if all items have a building set
    const allHaveBuilding = selectedItems.length > 0 &&
      selectedItems.every((item) => getBuildingId(item).trim() !== "");

    // Check if all items have the same building (only meaningful if all have buildings)
    const sameBuilding = allHaveBuilding &&
      selectedItems.every((item) => getBuildingId(item) === getBuildingId(selectedItems[0]));

    // Check if items have different buildings (for error message)
    const hasDifferentBuildings = allHaveBuilding && !sameBuilding;

    return {
      count: selectedItems.length,
      totalCost,
      sameBuilding,
      allHaveBuilding,
      hasDifferentBuildings,
      buildingId: sameBuilding ? getBuildingId(selectedItems[0]) : null,
      items: selectedItems,
    };
  }, [items, selectedRows]);

  // Get unique label types from renovation labels
  const labelTypes = useMemo(() => {
    const types = Array.from(new Set(renovationLabels.map((l) => l.type).filter(Boolean)));
    return types;
  }, [renovationLabels]);

  // Get available years from current year back to 2020
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push(year);
    }
    return years;
  }, []);

  // Filter bar sections
  const filterBarSections = useMemo(() => {
    return [
      {
        key: "status",
        label: "Status",
        options: Object.entries(INVESTMENT_STATUS_LABELS).map(([value, label]) => ({
          key: value,
          label,
        })),
        color: "moss" as const,
        selectedValues: statusFilter ? [statusFilter] : [],
        onUpdate: (values: string[]) => {
          setStatusFilter(values.length > 0 ? values[0] : undefined);
        },
      },
      {
        key: "building",
        label: "Gebouw",
        options: buildings.map((b) => ({ key: b.id, label: b.id })),
        selectedValues: buildingFilter ? [buildingFilter] : [],
        onUpdate: (values: string[]) => {
          setBuildingFilter(values.length > 0 ? values[0] : undefined);
        },
      },
      {
        key: "label",
        label: "Label",
        options: renovationLabels.map((l) => ({
          key: String(l.id),
          label: l.name,
        })),
        color: "brick" as const,
        selectedValues: labelFilter ? [String(labelFilter)] : [],
        onUpdate: (values: string[]) => {
          setLabelFilter(values.length > 0 ? parseInt(values[0], 10) : undefined);
        },
      },
      {
        key: "labelType",
        label: "Label categorie",
        options: labelTypes.map((type) => ({
          key: type,
          label: type,
        })),
        color: "neutral" as const,
        selectedValues: labelTypeFilter ? [labelTypeFilter] : [],
        onUpdate: (values: string[]) => {
          setLabelTypeFilter(values.length > 0 ? values[0] : undefined);
        },
      },
      {
        key: "year",
        label: "Jaar",
        options: availableYears.map((year) => ({
          key: String(year),
          label: String(year),
        })),
        color: "neutral" as const,
        selectedValues: yearFilter ? [String(yearFilter)] : [],
        onUpdate: (values: string[]) => {
          setYearFilter(values.length > 0 ? parseInt(values[0], 10) : undefined);
        },
      },
      {
        key: "unit",
        label: "Kostenplaats",
        options: units.map((u) => ({
          key: u.id,
          label: u.id,
        })),
        selectedValues: unitFilter ? [unitFilter] : [],
        onUpdate: (values: string[]) => {
          setUnitFilter(values.length > 0 ? values[0] : undefined);
        },
      },
      {
        key: "showConverted",
        label: "Projecten",
        options: [
          { key: "hide", label: "Verberg omgezette items" },
          { key: "show", label: "Toon alle items" },
        ],
        color: "neutral" as const,
        singleSelect: true,
        selectedValues: hideConverted ? ["hide"] : ["show"],
        onUpdate: (values: string[]) => {
          setHideConverted(values[0] === "hide" || values.length === 0);
        },
      },
    ];
  }, [statusFilter, buildingFilter, hideConverted, labelFilter, labelTypeFilter, yearFilter, unitFilter, renovationLabels, labelTypes, availableYears, buildings, units]);


  // Filter chips for FilterBar
  const filterBarChips = useMemo(() => {
    const chips: any[] = [];
    if (statusFilter) {
      chips.push({
        key: "status",
        value: statusFilter,
        label: INVESTMENT_STATUS_LABELS[statusFilter as InvestmentItemStatus],
      });
    }
    if (buildingFilter) {
      chips.push({
        key: "building",
        value: buildingFilter,
        label: buildingFilter,
      });
    }
    if (labelFilter) {
      const labelObj = renovationLabels.find((l) => l.id === labelFilter);
      chips.push({
        key: "label",
        value: String(labelFilter),
        label: labelObj?.name || `Label ${labelFilter}`,
      });
    }
    if (labelTypeFilter) {
      chips.push({
        key: "labelType",
        value: labelTypeFilter,
        label: `Categorie: ${labelTypeFilter}`,
      });
    }
    if (yearFilter) {
      chips.push({
        key: "year",
        value: String(yearFilter),
        label: `Jaar: ${yearFilter}`,
      });
    }
    if (unitFilter) {
      chips.push({
        key: "unit",
        value: unitFilter,
        label: `Kostenplaats: ${unitFilter}`,
      });
    }
    // Show chip when displaying converted items (not the default behavior)
    if (!hideConverted) {
      chips.push({
        key: "showConverted",
        value: "show",
        label: "Inclusief projecten",
      });
    }
    return chips;
  }, [statusFilter, buildingFilter, hideConverted, labelFilter, labelTypeFilter, yearFilter, unitFilter, renovationLabels]);

  // Handlers
  // Clicking a row opens the unified edit modal directly. The legacy
  // read-only drawer has been retired in favour of a single modal that
  // exposes every editable field and the audit trail.
  const handleRowClick = useCallback((item: InvestmentItem) => {
    setEditingItem(item);
    setEditModalOpen(true);
  }, []);

  const handleEditClick = useCallback((item: InvestmentItem) => {
    setEditingItem(item);
    setEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((item: InvestmentItem) => {
    setDeletingItem(item);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingItem) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/investment/items/${deletingItem.id}`,
        { method: "DELETE" },
        session
      );

      if (response.ok) {
        toast.success(`Item "${deletingItem.title}" verwijderd`);
        setDeleteModalOpen(false);
        setDeletingItem(null);
        fetchItems(false); // Refresh list
      } else {
        throw new Error("Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Fout bij verwijderen van item");
    }
  }, [deletingItem, session, fetchItems]);

  const handleReleaseClick = useCallback((item: InvestmentItem) => {
    setReleasingItem(item);
  }, []);

  const handleReleaseConfirm = useCallback(
    async (reason: string | undefined) => {
      if (!releasingItem) return;
      const total = Number(releasingItem.estimatedCostExclVat) || 0;
      const used = Number(releasingItem.allocatedBudget) || 0;
      const remainder = Math.max(0, total - used);
      const fmt = new Intl.NumberFormat("nl-BE", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      });
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${releasingItem.id}/release`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(reason ? { reason } : {}),
          },
          session,
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || "Failed to release item");
        }
        toast.success(
          `Lijn "${releasingItem.title}" vrijgegeven — ${fmt.format(remainder)} terug naar ${
            releasingItem.bucket?.name
              ? `"${releasingItem.bucket.name}"`
              : "het strategisch budget"
          }`,
        );
        setReleasingItem(null);
        fetchItems(false);
      } catch (error) {
        console.error("Error releasing item:", error);
        toast.error(
          error instanceof Error ? error.message : "Fout bij vrijgeven",
        );
      }
    },
    [releasingItem, session, fetchItems],
  );

  const handleConvertToProject = useCallback(() => {
    if (!selectedItemsStats.allHaveBuilding) {
      toast.error("Alle geselecteerde items moeten een gebouw hebben");
      return;
    }
    if (!selectedItemsStats.sameBuilding) {
      toast.error("Alle geselecteerde items moeten tot hetzelfde gebouw behoren");
      return;
    }
    setConvertModalOpen(true);
  }, [selectedItemsStats]);

  // Buckets for the currently filtered building. When no building filter is
  // active the picker shows an empty list to nudge users to filter first.
  // Hook is called up here so it's in scope for `handleUpdate` and the
  // picker config below.
  const { buckets: buildingBuckets } = useBudgetBuckets(buildingFilter);

  const handleUpdate = useCallback(
    (itemId: number, updates: any) => {
      // Optimistic update - immediately update local state
      setLocalItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id !== itemId) return item;

          const updatedItem = { ...item };

          // Handle different update types
          if (updates.labelId !== undefined) {
            // Find the label object from renovationLabels
            const label = renovationLabels.find((l) => l.id === updates.labelId);
            updatedItem.label = label || undefined;
          }
          if (updates.budgetLabel !== undefined) {
            updatedItem.budgetLabel = updates.budgetLabel;
          }
          if (updates.buildingId !== undefined) {
            updatedItem.buildingId = updates.buildingId;
            updatedItem.building = updates.buildingId ? { id: updates.buildingId } as any : undefined;
          }
          if (updates.unitIds !== undefined) {
            updatedItem.units = updates.unitIds.map((id: string) => ({ id })) as any;
          }
          if (updates.responsibleUserId !== undefined) {
            updatedItem.responsibleUserId = updates.responsibleUserId;
          }
          if (updates.title !== undefined) {
            updatedItem.title = updates.title;
          }
          if (updates.estimatedCostExclVat !== undefined) {
            updatedItem.estimatedCostExclVat = updates.estimatedCostExclVat;
          }
          if (updates.estimatedStartDate !== undefined) {
            updatedItem.estimatedStartDate = updates.estimatedStartDate;
          }
          if (updates.estimatedDeadline !== undefined) {
            updatedItem.estimatedDeadline = updates.estimatedDeadline;
          }
          if (updates.bucketId !== undefined) {
            // bucketId === null intentionally moves the line to overflow.
            const next = updates.bucketId
              ? buildingBuckets.find((b) => b.id === updates.bucketId)
              : null;
            updatedItem.bucket = next
              ? {
                  id: next.id,
                  name: next.name,
                  isApproved: next.isApproved,
                }
              : null;
          }

          return updatedItem;
        })
      );

      // Anything that touches the bucket math on the BE — moving the line
      // between pots or changing its amount — needs the bucket cards to
      // re-fetch so the headroom / overflow numbers stay in sync.
      const affectsBucket =
        updates.bucketId !== undefined ||
        updates.bucketAllocations !== undefined ||
        updates.estimatedCostExclVat !== undefined;
      if (affectsBucket && buildingFilter) {
        dispatchBudgetBucketsChanged(buildingFilter);
      }

      // Unit/kostenplaats changes only persist `{ id }` placeholders in the
      // optimistic state. Refetch so the row picks up the full unit data
      // (codes, names, building joins) the BE returns with the items list.
      if (updates.unitIds !== undefined) {
        fetchItems().catch((error) => {
          console.error(
            "[InvestmentItemsListPage] Failed to refresh after unit update:",
            error,
          );
        });
      }
    },
    [renovationLabels, buildingBuckets, buildingFilter, fetchItems]
  );

  // Render row helper
  const renderRow = useCallback(
    (item: InvestmentItem, context: any) => {
      return createInvestmentItemCells({
        item,
        context,
        handlers: {
          onRowClick: handleRowClick,
          onEditClick: handleEditClick,
          onDeleteClick: handleDeleteClick,
          onReleaseClick: handleReleaseClick,
        },
        users,
      });
    },
    [
      handleRowClick,
      handleEditClick,
      handleDeleteClick,
      handleReleaseClick,
      users,
    ]
  );

  // Totals row renderer — sums Nodig budget and In project across visible rows
  const renderFooterCells = useCallback(
    (visibleRows: InvestmentItem[]) => buildInvestmentFooterCells(visibleRows),
    [],
  );

  // Create dynamic picker config with renovation labels and budget buckets
  // as customOptions.
  const dynamicPickerConfig = useMemo(() => {
    const baseConfig = INVESTMENT_TABLE_CONFIG.pickerConfig;
    return {
      ...baseConfig,
      fieldMappings: baseConfig.fieldMappings.map((mapping) => {
        if (mapping.eventMode === "label") {
          return {
            ...mapping,
            customOptions: renovationLabels.map((label) => ({
              value: String(label.id),
              label: `${label.name} (${label.type})`,
            })),
          };
        }
        if (mapping.eventMode === "bucket") {
          return {
            ...mapping,
            customOptions: buildingBuckets.map((b) => ({
              value: String(b.id),
              label: `${b.name}${b.year ? ` (${b.year})` : ""}${
                b.isApproved ? "" : " · niet goedgekeurd"
              }`,
            })),
          };
        }
        return mapping;
      }),
    };
  }, [renovationLabels, buildingBuckets]);

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Header */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wallet size={32} weight="duotone" className="text-moss-primary" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Budget detailverdeling
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Verdeel strategische budgetten over labels, units en projecten
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Quick launcher: pick a bucket and open the split designer
                  to fan it out across labels in one go. Only enabled when
                  filtered to a single building so the bucket list is
                  unambiguous. */}
              {buildingFilter && buildingBuckets.length > 0 && (
                <Button
                  onClick={() => {
                    // Default to the first non-overflow bucket; user can
                    // switch via a small bucket picker inside the designer
                    // header if needed.
                    const target = buildingBuckets.find((b) => !b.isOverflow);
                    if (target) setSplitDesignerBucketId(target.id);
                    else
                      toast.info(
                        "Maak eerst een strategisch budget aan om budget te verdelen.",
                      );
                  }}
                  size="sm"
                  variant="outline"
                  className="border-moss-primary/40 text-moss-primary"
                >
                  <SquaresFour className="h-4 w-4" />
                  Verdeel strategisch budget
                </Button>
              )}
              <Button onClick={() => setCreateModalOpen(true)} size="sm">
                <Plus className="h-5 w-5" />
                Nieuw item
              </Button>
            </div>
          </div>
        </header>

        {/* Statistics Display */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex flex-wrap items-stretch gap-6">
            {/* Total Items */}
            <div className="flex flex-col">
              <span className="text-neutral-primary text-2xl font-bold">
                {items.length}
                {totalCount && totalCount > items.length && ` / ${totalCount}`}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                {hasActiveFilters ? "Resultaten" : "Budgetlijnen"}
              </span>
            </div>

            <div className="w-px bg-gray-300"></div>

            {/* Total Estimated Cost */}
            <div className="flex flex-col">
              <span className="text-moss-primary text-2xl font-bold">
                {new Intl.NumberFormat("nl-BE", {
                  style: "currency",
                  currency: "EUR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(statistics.totalCost)}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                Totale geschatte kosten
              </span>
            </div>

            <div className="w-px bg-gray-300"></div>

            {/* Status Breakdown */}
            <div className="flex flex-col gap-1">
              <span className="mb-1 text-xs font-semibold text-gray-500 uppercase">
                Status overzicht
              </span>
              <div className="flex gap-4">
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-700">
                    {statistics.byStatus[InvestmentItemStatus.ONTWERP]?.count || 0}
                  </span>
                  <span className="text-xs text-gray-500">Ontwerp</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-blue-700">
                    {statistics.byStatus[InvestmentItemStatus.IN_BEOORDELING]?.count || 0}
                  </span>
                  <span className="text-xs text-gray-500">In beoordeling</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-green-700">
                    {statistics.byStatus[InvestmentItemStatus.GOEDGEKEURD]?.count || 0}
                  </span>
                  <span className="text-xs text-gray-500">Goedgekeurd</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selection Action Bar */}
        {selectedRows.size > 0 && (
          <div className="sticky top-16 z-10 mx-6 my-4 rounded-lg border border-moss-primary/30 bg-moss-primary/10 p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900">
                  {selectedItemsStats.count} item(s) geselecteerd
                </span>
                <div className="h-4 w-px bg-gray-300"></div>
                <span className="text-sm font-semibold text-moss-primary">
                  Totaal:{" "}
                  {new Intl.NumberFormat("nl-BE", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(selectedItemsStats.totalCost)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRows(new Set())}
                >
                  Selectie wissen
                </Button>
                <Button
                  size="sm"
                  onClick={handleConvertToProject}
                  disabled={!selectedItemsStats.sameBuilding}
                  className="bg-moss-primary hover:bg-moss-primaryDark"
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Creëer renovatieproject
                </Button>
              </div>
            </div>
            {!selectedItemsStats.allHaveBuilding && selectedItemsStats.count > 0 && (
              <div className="mt-2 text-xs text-orange-600">
                <Warning className="inline h-3 w-3 mr-1" />
                Niet alle geselecteerde items hebben een gebouw ingesteld
              </div>
            )}
            {selectedItemsStats.hasDifferentBuildings && (
              <div className="mt-2 text-xs text-orange-600">
                <Warning className="inline h-3 w-3 mr-1" />
                Alle geselecteerde items moeten tot hetzelfde gebouw behoren
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <UniversalTable
          columnsConfig={INVESTMENT_TABLE_CONFIG.columnsConfig}
          pickerConfig={dynamicPickerConfig}
          sortConfig={INVESTMENT_TABLE_CONFIG.sortConfig}
          rows={items}
          groupBy={groupBy}
          title=""
          renderRow={renderRow}
          footerCells={renderFooterCells}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          onUpdate={handleUpdate}
          isLoading={isLoading}
        />

        {/* Lazy Load Trigger */}
        {hasMore && !hasActiveFilters && (
          <LazyLoadTrigger
            observerRef={observerTarget}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadingMessage="Meer items laden..."
          />
        )}
      </div>

      {/* FilterBar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Zoek items op titel, beschrijving..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={true}
        urlParamPrefix=""
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <Warning size={24} className="text-red-600" weight="fill" />
              </div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Item verwijderen
              </DialogTitle>
            </div>
          </DialogHeader>
          <DialogDescription className="text-gray-600">
            Weet u zeker dat u het item{" "}
            <strong className="text-gray-900">{deletingItem?.title}</strong> wilt
            verwijderen? Deze actie kan niet ongedaan worden gemaakt.
          </DialogDescription>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingItem(null);
              }}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash className="h-4 w-4 mr-2" />
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Investment Item Modal */}
      <InvestmentItemModal
        isOpen={createModalOpen || editModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditModalOpen(false);
          setEditingItem(null);
        }}
        onSuccess={handleModalSuccess}
        editingItem={editingItem}
      />

      {/* Release dialog — confirm + capture optional audit reason */}
      <ReleaseInvestmentItemDialog
        isOpen={releasingItem !== null}
        item={releasingItem}
        onClose={() => setReleasingItem(null)}
        onConfirm={handleReleaseConfirm}
      />

      {/* Multi-pot bucket allocation dialog */}
      <BucketAllocationDialog
        item={bucketAllocItem}
        isOpen={bucketAllocItem !== null}
        onClose={() => setBucketAllocItem(null)}
        onSaved={() => {
          setBucketAllocItem(null);
          fetchItems(false);
        }}
      />

      {/* Convert to Project Modal */}
      <ConvertToProjectModal
        isOpen={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        onSuccess={() => {
          fetchItems(false); // Refresh the list
          setSelectedRows(new Set()); // Clear selection
        }}
        selectedItems={selectedItemsStats.items}
        buildingId={selectedItemsStats.buildingId || ""}
      />


      {/* Budget Change Modal (with reason for audit trail) */}
      <ChangeWithReasonModal
        isOpen={budgetChangeModalOpen}
        onClose={() => {
          setBudgetChangeModalOpen(false);
          setBudgetChangeItem(null);
        }}
        onSuccess={() => {
          fetchItems(false); // Refresh the list
        }}
        item={budgetChangeItem}
        changeType="budget"
        onTransferRequest={handleTransferRequest}
      />

      {/* Status Change Modal (with reason for audit trail) */}
      <ChangeWithReasonModal
        isOpen={statusChangeModalOpen}
        onClose={() => {
          setStatusChangeModalOpen(false);
          setStatusChangeItem(null);
        }}
        onSuccess={() => {
          fetchItems(false); // Refresh the list
        }}
        item={statusChangeItem}
        changeType="status"
      />

      {/* Budget Transfer Modal */}
      {transferSourceItem && (
        <BudgetTransferModal
          isOpen={transferModalOpen}
          onClose={() => {
            setTransferModalOpen(false);
            setTransferSourceItem(null);
            setTransferFreedAmount(0);
          }}
          onSuccess={() => {
            fetchItems(false); // Refresh the list
          }}
          sourceItem={transferSourceItem}
          freedAmount={transferFreedAmount}
        />
      )}

      {/* Budget detailverdeling designer — fast split of a bucket into
          multiple investment lines (labels + amounts + units). */}
      <BudgetSplitDesigner
        bucket={
          splitDesignerBucketId
            ? buildingBuckets.find((b) => b.id === splitDesignerBucketId) ??
              null
            : null
        }
        isOpen={splitDesignerBucketId !== null}
        onClose={() => setSplitDesignerBucketId(null)}
        onSaved={() => {
          fetchItems(false);
        }}
      />
    </>
  );
};

export const InvestmentItemsListPage = memo(InvestmentItemsListPageComponent);
